// Renders a single Executive Recommendation with Explain / Dismiss / Defer /
// Mark Done actions and a link out to the source record. Used by the
// Executive Brief, Action Center, and the assistant's proposal surface.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ChevronRight,
  Info,
  X,
  Check,
  Clock,
  ExternalLink,
  Loader2,
  UserCheck,
} from "lucide-react";
import {
  ExecRecommendation,
  priorityBadgeClass,
  recordRecommendationResponse,
  useInvalidateSnapshot,
} from "@/lib/executive/engine";
import { ExplainDialog } from "./ExplainDialog";
import { toast } from "@/hooks/use-toast";

export function RecommendationCard({ rec }: { rec: ExecRecommendation }) {
  const [explainOpen, setExplainOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const invalidate = useInvalidateSnapshot();

  const act = async (resp: "dismiss" | "complete" | "defer" | "waiting") => {
    setBusy(resp);
    try {
      const deferUntil =
        resp === "defer" ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() : undefined;
      await recordRecommendationResponse(rec.id, resp, undefined, deferUntil);
      toast({
        title:
          resp === "complete"
            ? "Marked done"
            : resp === "dismiss"
            ? "Dismissed"
            : resp === "waiting"
            ? "Added to Waiting On Me"
            : "Deferred 7 days",
      });
      invalidate();
    } catch (e: any) {
      toast({ title: "Failed", description: e?.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };


  const link = rec.linked_records?.[0];

  return (
    <Card className="p-3 border-l-4" style={{ borderLeftColor: priorityAccent(rec.priority) }}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={priorityBadgeClass(rec.priority)}>{rec.priority.toUpperCase()}</Badge>
            <div className="font-medium text-sm">{rec.title}</div>
          </div>
          {rec.summary && (
            <div className="text-xs text-muted-foreground mt-1">{rec.summary}</div>
          )}
          {rec.recommendation && (
            <div className="text-xs mt-1">
              <span className="font-medium">Recommendation: </span>
              {rec.recommendation}
            </div>
          )}
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setExplainOpen(true)}>
              <Info className="h-3 w-3 mr-1" /> Explain
            </Button>
            {link?.href && (
              <Button asChild size="sm" variant="ghost" className="h-7 px-2">
                <Link to={link.href}>
                  Open <ExternalLink className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => act("complete")} disabled={!!busy}>
              {busy === "complete" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />} Done
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => act("defer")} disabled={!!busy}>
              {busy === "defer" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Clock className="h-3 w-3 mr-1" />} Defer
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => act("waiting")} disabled={!!busy}>
              {busy === "waiting" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UserCheck className="h-3 w-3 mr-1" />} Mark Waiting
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground" onClick={() => act("dismiss")} disabled={!!busy}>
              {busy === "dismiss" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <X className="h-3 w-3 mr-1" />} Dismiss
            </Button>

          </div>
        </div>
      </div>
      <ExplainDialog open={explainOpen} onOpenChange={setExplainOpen} recommendation={rec} />
    </Card>
  );
}

function priorityAccent(p: string): string {
  switch (p) {
    case "critical":
      return "hsl(var(--destructive))";
    case "high":
      return "#f97316";
    case "medium":
      return "#f59e0b";
    default:
      return "hsl(var(--muted))";
  }
}
